/**
 * What Home decides, kept apart from how it draws it (UX_CONCEPT.md §5.1, §6).
 *
 * Everything here is computed from data the traveller actually has. The old
 * dashboard told everyone "Health Status: Optimal — all your health data is
 * current and complete" from a hardcoded string, and called a reading with no
 * date current by substituting today's date for the missing one.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

/** A reading older than this is worth renewing before a trip. */
export const STALE_AFTER_DAYS = 90;

export type Tone = "ready" | "attention" | "neutral";

export interface VitalStatus {
  key:
    | "low"
    | "normal"
    | "elevated"
    | "high"
    | "underweight"
    | "overweight"
    | "obese";
  tone: Tone;
  /** Whether a fresh reading should be suggested on account of the value. */
  abnormal: boolean;
}

/** Thresholds of the 2017 ACC/AHA guideline. */
export function bloodPressureStatus(
  systolic: number,
  diastolic: number,
): VitalStatus {
  if (systolic < 90 || diastolic < 60)
    return { key: "low", tone: "attention", abnormal: true };
  if (systolic >= 130 || diastolic >= 80)
    return { key: "high", tone: "attention", abnormal: true };
  if (systolic >= 120)
    return { key: "elevated", tone: "attention", abnormal: false };
  return { key: "normal", tone: "ready", abnormal: false };
}

export function pulseStatus(bpm: number): VitalStatus {
  if (bpm < 60) return { key: "low", tone: "neutral", abnormal: false };
  if (bpm > 100) return { key: "high", tone: "attention", abnormal: true };
  return { key: "normal", tone: "ready", abnormal: false };
}

export function bmiStatus(bmi: number): VitalStatus {
  if (bmi < 18.5)
    return { key: "underweight", tone: "attention", abnormal: false };
  if (bmi < 25) return { key: "normal", tone: "ready", abnormal: false };
  if (bmi < 30) return { key: "overweight", tone: "neutral", abnormal: false };
  return { key: "obese", tone: "neutral", abnormal: false };
}

export type ReadingAge =
  | { kind: "unknown" }
  | { kind: "today" }
  | { kind: "days"; n: number }
  | { kind: "months"; n: number }
  | { kind: "years"; n: number };

/** How old a reading is, in the unit a person would say it in. */
export function readingAge(
  iso: string | null | undefined,
  now: Date,
): ReadingAge {
  if (!iso) return { kind: "unknown" };
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return { kind: "unknown" };
  const days = Math.max(0, Math.floor((now.getTime() - at) / DAY_MS));
  if (days === 0) return { kind: "today" };
  if (days < 60) return { kind: "days", n: days };
  if (days < 365) return { kind: "months", n: Math.floor(days / 30) };
  return { kind: "years", n: Math.floor(days / 365) };
}

/** A reading with no date counts as stale: nothing says it is recent. */
export function isStale(iso: string | null | undefined, now: Date): boolean {
  if (!iso) return true;
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return true;
  return now.getTime() - at > STALE_AFTER_DAYS * DAY_MS;
}

/** Whole days from today to `iso`, counting calendar days, or null. */
export function daysUntil(
  iso: string | null | undefined,
  now: Date,
): number | null {
  if (!iso) return null;
  const target = new Date(iso);
  if (Number.isNaN(target.getTime())) return null;
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - start.getTime()) / DAY_MS);
}

export interface BloodPressureReading {
  systolic: number;
  diastolic: number;
  recordedAt?: string | null;
}

export interface NextStepInput {
  /** From the card's emergency summary; null when the card has none. */
  bloodType: string | null | undefined;
  bloodPressure: BloodPressureReading | null;
  nextTrip: {
    id: string;
    destination: string;
    /** From the trip's checklist; null when none was built yet. */
    readinessPercent?: number | null;
  } | null;
  dismissed: ReadonlySet<string>;
  now: Date;
}

export type NextStep =
  | { id: "emergency" }
  | { id: "bp"; reason: "abnormal" | "stale"; reading: BloodPressureReading }
  | { id: "trip"; tripId: string; destination: string }
  | { id: "plan" };

/**
 * The one nudge Home shows (UX_CONCEPT.md §6.2), or null.
 *
 * Order: emergency basics, then a safety-relevant reading, then the next
 * trip, then planning one. Deadline-driven readiness items slot in second once
 * the readiness model exists (step 5). A reading that was never taken is not
 * nagged about; one that is abnormal or old is.
 */
export function nextBestStep(input: NextStepInput): NextStep | null {
  const { dismissed } = input;

  if (!input.bloodType && !dismissed.has("emergency")) {
    return { id: "emergency" };
  }

  const bp = input.bloodPressure;
  if (bp && !dismissed.has("bp")) {
    if (bloodPressureStatus(bp.systolic, bp.diastolic).abnormal) {
      return { id: "bp", reason: "abnormal", reading: bp };
    }
    if (isStale(bp.recordedAt, input.now)) {
      return { id: "bp", reason: "stale", reading: bp };
    }
  }

  if (input.nextTrip) {
    // A trip that is already ready needs no nudge.
    if (
      input.nextTrip.readinessPercent !== 100 &&
      !dismissed.has(`trip:${input.nextTrip.id}`)
    ) {
      return {
        id: "trip",
        tripId: input.nextTrip.id,
        destination: input.nextTrip.destination,
      };
    }
    return null;
  }

  if (!dismissed.has("plan")) {
    return { id: "plan" };
  }
  return null;
}

/** The key a dismissal is stored under; a trip nudge is dismissed per trip. */
export function dismissKey(step: NextStep): string {
  return step.id === "trip" ? `trip:${step.tripId}` : step.id;
}
